'use client';

import type { ProductDetailSpec, ProductDetailNotice } from 'src/types/product-rankings/product-rankings-ops';

import { useState } from 'react';

import { Box, Stack, Button, Divider, Collapse, Typography } from '@mui/material';

import { RADIUS } from 'src/theme/ui-tokens';

import { Iconify } from 'src/components/iconify';

import SectionHeading from './market-rankings-section-heading';


/** 원문(통합 안 된 고시정보)을 접었을 때 보이는 줄 수 */
const SPEC_PREVIEW_ROWS = 5;

/** 한 항목의 값이 이보다 길면 접는다 — 전성분은 400자를 넘는 것이 흔하다 */
const NOTICE_PREVIEW_CHARS = 260;

/**
 * 고시정보 한 줄. 값이 길면 접고, 펼치는 버튼에 **글자 수를 밝힌다** —
 * 얼마나 긴지 모르면 펼칠지 말지 못 정한다.
 */
function NoticeRow({
  label,
  value,
  highlight,
}: {
  label: string;
  value: string;
  highlight?: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const long = value.length > NOTICE_PREVIEW_CHARS;
  const shown = expanded || !long ? value : `${value.slice(0, NOTICE_PREVIEW_CHARS)}…`;

  return (
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 0.25, sm: 1.5 }} sx={{ p: 1.5 }}>
      <Typography
        variant="caption"
        sx={{
          fontWeight: 600,
          color: highlight ? 'primary.main' : 'text.secondary',
          width: { sm: 130 },
          flexShrink: 0,
        }}
      >
        {label}
      </Typography>
      <Stack spacing={0.25} sx={{ minWidth: 0 }}>
        <Typography sx={{ typography: 'uiMeta', lineHeight: 1.6, whiteSpace: 'pre-line', minWidth: 0 }}>
          {shown}
        </Typography>
        {long ? (
          <Box
            component="button"
            type="button"
            onClick={() => setExpanded((prev) => !prev)}
            sx={{
              alignSelf: 'flex-start',
              p: 0,
              border: 0,
              bgcolor: 'transparent',
              cursor: 'pointer',
              typography: 'uiAction',
              color: 'primary.main',
            }}
          >
            {expanded ? '접기' : `전체 보기 (${value.length.toLocaleString('ko-KR')}자)`}
          </Box>
        ) : null}
      </Stack>
    </Stack>
  );
}

/**
 * 상품 정보(고시정보).
 *
 * ## ⚠️ 통합본을 먼저 보여 준다
 *
 * 판매처마다 같은 항목을 다른 라벨로 준다(올영 "화장품법에 따라 기재해야 하는 모든 성분",
 * 무신사 "화장품법에 따라 기재, 표시하여야 하는 모든 성분"). 그대로 늘어놓으면 상품끼리
 * 나란히 못 본다 — 서버가 몰 무관 8키로 통합한 `notice`를 먼저 세우고, 원본 라벨은
 * 토글로 편다.
 *
 * ⚠️ 통합 항목이 하나도 안 잡히는 상품이 있다(고시 형식이 다르다). 그때는 **원본을 그대로**
 * 보여 준다 — 그 경우에만 다섯 줄로 접는다.
 *
 * ⚠️ `truncated`는 **수집 때 원문이 잘렸다**는 뜻이다. 화면이 접은 것과 다른 말이라
 * 따로 밝힌다 — 안 밝히면 "더 보기"를 눌러도 안 나오는 항목을 버그로 읽는다.
 */
export default function SpecBlock({
  notice,
  spec,
}: {
  notice?: ProductDetailNotice | null;
  spec?: ProductDetailSpec | null;
}) {
  const [expanded, setExpanded] = useState(false);
  const [showRaw, setShowRaw] = useState(false);

  const noticeItems = notice?.items ?? [];
  const rawItems = spec?.items ?? [];
  if (!noticeItems.length && !rawItems.length) return null;

  const useRaw = noticeItems.length === 0;
  const items = useRaw ? rawItems : noticeItems;
  const rows = useRaw && !expanded ? items.slice(0, SPEC_PREVIEW_ROWS) : items;

  return (
    <>
      <Divider sx={{ my: 2 }} />
      <SectionHeading
        icon="solar:clipboard-list-linear"
        iconColor="text.secondary"
        title="상품 정보"
        count={useRaw ? undefined : `${items.length}항목`}
        hint={spec?.category || undefined}
      />
      <Stack
        divider={<Divider flexItem />}
        sx={{ borderRadius: RADIUS.md, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}
      >
        {rows.map((item, index) => (
          <NoticeRow
            key={`${'key' in item ? item.key : item.label}-${index}`}
            label={item.label}
            value={item.value || '-'}
            highlight={'key' in item && item.key === '전성분'}
          />
        ))}
      </Stack>

      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        flexWrap="wrap"
        useFlexGap
        sx={{ mt: 1 }}
      >
        {useRaw && items.length > SPEC_PREVIEW_ROWS ? (
          <Button
            size="small"
            onClick={() => setExpanded((prev) => !prev)}
            endIcon={
              <Iconify
                icon={expanded ? 'solar:alt-arrow-up-linear' : 'solar:alt-arrow-down-linear'}
                width={16}
              />
            }
          >
            {expanded ? '접기' : `${items.length - SPEC_PREVIEW_ROWS}개 항목 더 보기`}
          </Button>
        ) : null}
        {!useRaw && rawItems.length ? (
          <Button
            size="small"
            color="inherit"
            onClick={() => setShowRaw((prev) => !prev)}
            endIcon={
              <Iconify
                icon={showRaw ? 'solar:alt-arrow-up-linear' : 'solar:alt-arrow-down-linear'}
                width={16}
              />
            }
          >
            {showRaw ? '판매처 원문 접기' : `판매처 원문 고시정보 (${rawItems.length}항목)`}
          </Button>
        ) : null}
        {spec?.truncated || notice?.truncated ? (
          <Typography variant="caption" sx={{ color: 'text.disabled' }}>
            원문이 길어 수집 시 일부가 잘렸습니다.
          </Typography>
        ) : null}
      </Stack>

      {!useRaw && rawItems.length ? (
        <Collapse in={showRaw}>
          <Stack
            divider={<Divider flexItem />}
            sx={{
              mt: 1,
              borderRadius: RADIUS.md,
              border: '1px solid',
              borderColor: 'divider',
              overflow: 'hidden',
              bgcolor: 'grey.50',
            }}
          >
            {rawItems.map((item, index) => (
              <NoticeRow key={`raw-${item.label}-${index}`} label={item.label} value={item.value || '-'} />
            ))}
          </Stack>
        </Collapse>
      ) : null}
    </>
  );
}
